import { Component, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbModule, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { ItemSearchResponseModel } from './models/item-search-response-model';
import { ItemsService } from './items.service';
import { DropDownListModel } from '../models/dropdown-list-model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ViewItemComponent } from './child-components/view-item.component';
import { EditItemSuppliersComponent } from './child-components/edit-item-suppliers.component';
import { AddItemComponent } from './child-components/add-item.component';
import { ViewJsonComponent } from './child-components/view-json.component';
import { AuthService } from '../auth.service';
import { FilterService, GridDataResult, KENDO_GRID, PageChangeEvent, PagerSettings, SelectableSettings, SelectionEvent } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import { Observable } from 'rxjs';
import { SupplierDetailComponent } from './supplier-detail.component';
import { SupplierResponseModel } from './models/supplier-response-model';

@Component({
  selector: 'items',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbModule, NgbNavModule, KENDO_GRID, SupplierDetailComponent],
  providers: [ItemsService],
  templateUrl: './items.component.html',
})
export class ItemsComponent implements OnInit  {
  public view: Observable<GridDataResult>;
  public selectedItemIds: string[] = [];
  public error: boolean = false;
  public errorMessage: string = '';
  public showSpinner: boolean = false;
  public validationMessage: string = '';
  public successMessage: string = '';
  public rowDefs: ItemSearchResponseModel[] = [];
  public rowHeight = 32;
  public isAdmin: boolean = false;
  public isReader: boolean = false;
  public selectedItems: ItemSearchResponseModel[] = [];

  public selectableSettings: SelectableSettings = {
    checkboxOnly: true,
    mode: 'multiple'
  };

  public state: State = {
    skip: 0,
    take: 20,
  };
  
  public pageableSettings: PagerSettings = {
    pageSizes: [10, 20, 50, 100]
  };

  public filter: CompositeFilterDescriptor = {
    logic: "and",
    filters: [],
  };

  public itemStateOptions: { value: string; text: string }[] = [
    { value: '10', text: '10 (NEW)' },
    { value: '20', text: '20 (ACTIVE)' },
    { value: '30', text: '30 (INACTIVE)' },
    { value: '40', text: '40 (DELETED)' }
  ];

  public isReplacedOptions: { value: string; text: string }[] = [
    { value: 'true', text: 'YES' },
    { value: 'false', text: 'NO' }
  ];

  public itemStateDropdownOptions: DropDownListModel[] = [
    { value: '10', text: 'NEW' },
    { value: '20', text: 'ACTIVE' },
    { value: '30', text: 'INACTIVE' },
    { value: '40', text: 'DELETED' }
  ];

  public isReplacedDropdownOptions: DropDownListModel[] = [
    { value: 'true', text: 'YES' },
    { value: 'false', text: 'NO' }
  ];

  public suppliersMap: Map<string, SupplierResponseModel[]> = new Map();
  public loadingSuppliers: Set<string> = new Set();
  public supplierErrorMap: Map<string, string> = new Map();

  constructor(public itemsService: ItemsService, private modalService: NgbModal, private authService: AuthService) {
    this.view = itemsService;
    this.itemsService.query(this.state, this.filter);
  }

  ngOnInit() {
    this.authService.getRoles().subscribe({
      next: (roles) => {
        this.isAdmin = roles.includes('ItemRegistry.Admin');
        this.isReader = roles.includes('ItemRegistry.Read');
      },
      error: (error) => {
        console.error('Error loading roles:', error);
        this.isAdmin = false;
        this.isReader = false;
      }
    });
  }

  public addItem(): void {
    if (!this.isAdmin) return;
    const modalRef = this.modalService.open(AddItemComponent, {
      size: 'xl',
      scrollable: true,
      backdrop: true,
      keyboard: false,
      ariaLabelledBy: 'add-item-title'
    });
    modalRef.result.then((result) => {
      if (result?.action === 'save') {
        this.successMessage = `Item ${result.itemId} added successfully!`;
        this.itemsService.query(this.state, this.filter);
        setTimeout(() => { this.successMessage = ''; }, 3000);
      }
    }, () => {});
  }

 public editItem(dataItem: ItemSearchResponseModel): void {
    const isManualImport = dataItem.publishingSource === 'MANUAL_IMPORT';
    const modalRef = isManualImport
      ? this.modalService.open(AddItemComponent, {
          size: 'xl',
          scrollable: true,
          backdrop: true,
          keyboard: false,
          ariaLabelledBy: 'add-item-title'
        })
      : this.modalService.open(EditItemSuppliersComponent, { size: 'lg' });
    modalRef.componentInstance.itemModel = dataItem;
    modalRef.result.then((result) => {
      if (result?.action === 'save') {
        this.itemsService.query(this.state, this.filter);
      }
    }, () => {});
  }

  public viewItem(dataItem: ItemSearchResponseModel): void {
    const modalRef = this.modalService.open(ViewItemComponent, { size: 'size_60' });
    modalRef.componentInstance.itemModel = dataItem;
  }

  public resendItem(gridRow: ItemSearchResponseModel) {
    this.showSpinner = true;
    this.successMessage = '';
    this.error = false;
    this.errorMessage = '';
    
    this.itemsService.resendItems([gridRow.itemId]).subscribe({
      next: (result) => {
        this.showSpinner = false;
        this.successMessage = `Item ${gridRow.itemId} resent successfully!`;
        
        // Fade away the success message after 3 seconds
        setTimeout(() => {
          this.successMessage = '';
        }, 3000);
      },
      error: (error) => {
        console.error('Error resending item:', error);
        this.showSpinner = false;
        this.errorMessage = 'Failed to resend item. Please try again.';
        this.error = true;
      },
      complete: () => {
        this.showSpinner = false;
      }
    });
  }

  public resendSelectedItems(): void {
    const itemIds = this.selectedItems.map(i => i.itemId);
    this.showSpinner = true;
    this.successMessage = '';
    this.error = false;
    this.errorMessage = '';

    this.itemsService.resendItems(itemIds).subscribe({
      next: () => {
        this.showSpinner = false;
        this.successMessage = `${itemIds.length} item(s) resent successfully!`;
        this.selectedItems = [];
        this.selectedItemIds = [];
        setTimeout(() => { this.successMessage = ''; }, 3000);
      },
      error: () => {
        this.showSpinner = false;
        this.errorMessage = `Failed to resend ${itemIds.length} item(s). Please try again.`;
        this.error = true;
      }
    });
  }

  public viewSyncJson(gridRow: ItemSearchResponseModel) {
    console.log(`Fetching sync JSON for Item: ` + gridRow.itemId);
    
    this.showSpinner = true;
    this.error = false;
    this.errorMessage = '';
    
    this.itemsService.getSyncItemMaster(gridRow.itemId).subscribe({
      next: (result) => {
        console.log('Sync JSON retrieved successfully:', result);
        this.showSpinner = false;
        
        const modalRef = this.modalService.open(ViewJsonComponent, { size: 'xl', scrollable: true });
        modalRef.componentInstance.jsonData = result;
        modalRef.componentInstance.title = `Sync Item Master JSON - ${gridRow.itemId}`;
      },
      error: (error) => {
        console.error('Error fetching sync JSON:', error);
        this.showSpinner = false;
        this.errorMessage = 'Failed to retrieve sync JSON. Please try again.';
        this.error = true;
      },
      complete: () => {
        this.showSpinner = false;
      }
    });
  }

  public getIsReplacedFilterValue(filter: CompositeFilterDescriptor, field: string): string {
    const descriptor = this.findFieldFilter(filter, field);
    if (!descriptor || descriptor.value === undefined || descriptor.value === null) {
      return '';
    }

    return String(descriptor.value);
  }

  private findFieldFilter(filter: CompositeFilterDescriptor, field: string): FilterDescriptor | null {
    for (const item of filter.filters) {
      if (this.isCompositeFilter(item)) {
        const nestedFilter = this.findFieldFilter(item, field);
        if (nestedFilter) {
          return nestedFilter;
        }
      } else if (item.field === field) {
        return item;
      }
    }

    return null;
  }
  
  private isCompositeFilter(item: FilterDescriptor | CompositeFilterDescriptor): item is CompositeFilterDescriptor {
    return 'filters' in item;
  }

  public getItemStateText(itemState: number | string | null | undefined): string {
    switch (Number(itemState)) {
      case 10:
        return 'NEW';
      case 20:
        return 'ACTIVE';
      case 30:
        return 'INACTIVE';
      case 40:
        return 'OBSOLETE';
      case 99:
        return 'DELETED';
      default:
        return itemState == null ? '' : String(itemState);
    }
  }

  public getItemStateFilterValue(filter: CompositeFilterDescriptor, field: string): string {
    const descriptor = this.findFieldFilter(filter, field);
    return descriptor ? String(descriptor.value ?? '') : '';
  }

  public onItemStateFilterChange(
    value: string,
    filterService: FilterService,
    filter: CompositeFilterDescriptor,
    field: string
  ): void {
    const updatedFilter: CompositeFilterDescriptor = {
      logic: this.filter?.logic ?? 'and',
      filters: this.removeFieldFilter(this.filter?.filters ?? [], field)
    };

    if (value !== '') {
      updatedFilter.filters.push({
        field,
        operator: 'eq',
        value: Number(value)
      });
    }

    this.filterChange(updatedFilter);
  }

  private removeFieldFilter(filters: Array<FilterDescriptor | CompositeFilterDescriptor>, field: string): Array<FilterDescriptor | CompositeFilterDescriptor> {
    const cleanedFilters: Array<FilterDescriptor | CompositeFilterDescriptor> = [];

    for (const item of filters) {
      if (this.isCompositeFilter(item)) {
        const nestedFilters = this.removeFieldFilter(item.filters, field);
        if (nestedFilters.length > 0) {
          cleanedFilters.push({
            logic: item.logic,
            filters: nestedFilters
          });
        }
      } else if (item.field !== field) {
        cleanedFilters.push(item);
      }
    }

    return cleanedFilters;
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.filter = filter;
    this.state.skip = 0; // reset the page index when the filter changes
    this.itemsService.query(this.state, this.filter);
  }

  public onIsReplacedFilterChange(
    value: string,
    filterService: FilterService,
    filter: CompositeFilterDescriptor,
    field: string
  ): void {
    const updatedFilter: CompositeFilterDescriptor = {
      logic: this.filter?.logic ?? 'and',
      filters: this.removeFieldFilter(this.filter?.filters ?? [], field)
    };

    if (value !== '') {
      updatedFilter.filters.push({
        field,
        operator: 'eq',
        value: value === 'true'
      });
    }

    this.filterChange(updatedFilter);
  }

  public pageChange(state: PageChangeEvent): void {
    this.state.skip = state.skip;
    this.state.take = state.take;
    this.itemsService.query(this.state, this.filter);
  }

  public onSelectionChange(event: SelectionEvent): void {
    event.selectedRows.forEach(row => {
      if (!this.selectedItems.find(i => i.itemId === row.dataItem.itemId)) {
        this.selectedItems.push(row.dataItem);
      }
    });
    event.deselectedRows.forEach(row => {
      this.selectedItems = this.selectedItems.filter(i => i.itemId !== row.dataItem.itemId);
    });
    this.selectedItemIds = this.selectedItems.map(i => i.itemId);
  }

  public refreshData(): void {
    this.itemsService.query(this.state, this.filter);
  }

  public onDetailExpand(event: { dataItem: ItemSearchResponseModel }): void {
    const itemId = event.dataItem.itemId;
    if (this.suppliersMap.has(itemId)) {
      return; // already loaded
    }
    this.loadingSuppliers.add(itemId);
    this.supplierErrorMap.delete(itemId);
    this.itemsService.getSuppliers(itemId).subscribe({
      next: (suppliers) => {
        this.suppliersMap.set(itemId, suppliers);
        this.loadingSuppliers.delete(itemId);
      },
      error: () => {
        this.loadingSuppliers.delete(itemId);
        this.supplierErrorMap.set(itemId, 'Failed to load suppliers.');
      }
    });
  }

}